import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

/**
 * Deletion of a user or a company by an admin (epic BDA-6793, BDA-6799):
 * deletion check → optional content purge (job chain) → delete (job chain).
 * Every endpoint needs ADMIN:USER.
 */

/** A shareable that keeps its owner from being deleted. */
export interface BdOwnerDeletionBlockingItem {
  id: string;
  /** Shareable type, an open set (`virtual-clinic`, `market`, …). */
  type: string;
  name: string | null;
  folderId: string;
  /** Top-most folder down to the item's folder. */
  folderPath: string[];
  inRecycleBin: boolean;
  temporary: boolean;
}

/** `GET /users/{id}/deletion-check`, and `resources` of the company check. */
export interface BdOwnerDeletionCheck {
  empty: boolean;
  blockingCount: number;
  blockingCountByType: Record<string, number>;
  /** The first blocking items, at most 50. */
  blockingItems: BdOwnerDeletionBlockingItem[];
}

/** `GET /companies/{id}/deletion-check`. */
export interface BdCompanyDeletionCheck {
  /** Also counts the content of the company's soft-deleted groups. */
  resources: BdOwnerDeletionCheck;
  activeGroupCount: number;
  activeDirectMemberCount: number;
}

/** 202 of the content purge and of the delete endpoints. */
export interface BdOwnerChainStarted {
  chainId: string;
}

export type BdContentPurgeUnitOutcome =
  | 'PURGED'
  | 'GONE'
  | 'SKIP'
  | 'NOT_SUPPORTED'
  | 'FAILED';

export interface BdContentPurgeUnit {
  type: string;
  id: string;
  outcome: BdContentPurgeUnitOutcome;
  reason: string | null;
}

/** Result of the `de-system.owner-content-purge` task. */
export interface BdContentPurgeResult {
  counts: {
    purged: number;
    gone: number;
    skipped: number;
    notSupported: number;
    failed: number;
    total: number;
  };
  /** All units, or — above 200 — only those not PURGED or GONE. */
  units: BdContentPurgeUnit[];
  allUnitsListed: boolean;
  /** The deletion check after the purge; scenarios (NOT_SUPPORTED) keep it `false`. */
  empty: boolean;
}

/**
 * What `DELETE /users/{id}` or `DELETE /companies/{id}` answered:
 * - `started`: 202, the delete chain runs (or a pending user deletion restarted).
 * - `already-deleted`: 204, the user is deleted completely.
 * - `not-found`: 404, the owner is unknown (a company is gone once the DB
 *   step of its delete chain ran).
 * - `self-delete`: 409 SELF_DELETE, the admin's own user.
 * - `not-empty`: 409 OWNER_NOT_EMPTY / COMPANY_NOT_EMPTY, content (or groups,
 *   members) appeared since the check. `check` is the deletion check the
 *   409 carries, `null` when its body has another shape.
 */
export type BdOwnerDeleteOutcome<TCheck = unknown> =
  | { kind: 'started'; chainId: string }
  | { kind: 'already-deleted' }
  | { kind: 'not-found' }
  | { kind: 'self-delete'; message: string }
  | { kind: 'not-empty'; message: string; check: TCheck | null };

/** A company can be deleted only without content, active groups and direct members. */
export function bdCompanyDeletable(check: BdCompanyDeletionCheck): boolean {
  return (
    check.resources.empty &&
    check.activeGroupCount === 0 &&
    check.activeDirectMemberCount === 0
  );
}

/** Maps a successful delete response (202 with a chain, 204 without). */
export function bdOwnerDeleteStarted(
  response: HttpResponse<BdOwnerChainStarted | null>,
): BdOwnerDeleteOutcome<never> {
  const chainId = response.body?.chainId;
  return response.status === 202 && chainId
    ? { kind: 'started', chainId }
    : { kind: 'already-deleted' };
}

/**
 * Maps a 404 or a 409 of the delete endpoints; `null` for every other error.
 * `checkOf` reads the deletion check that a 409 not-empty body carries next
 * to `code` and `message` (the backend unwraps it into the body).
 */
export function bdOwnerDeleteRejection<TCheck = never>(
  error: unknown,
  checkOf: (body: Record<string, unknown>) => TCheck | null = () => null,
): BdOwnerDeleteOutcome<TCheck> | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }
  if (error.status === 404) {
    return { kind: 'not-found' };
  }
  const body: unknown = error.error;
  if (error.status !== 409 || !isRecord(body)) {
    return null;
  }
  const message = typeof body['message'] === 'string' ? body['message'] : '';
  switch (body['code']) {
    case 'SELF_DELETE':
      return { kind: 'self-delete', message };
    case 'OWNER_NOT_EMPTY':
    case 'COMPANY_NOT_EMPTY':
      return { kind: 'not-empty', message, check: checkOf(body) };
    default:
      return null;
  }
}

/** A user deletion check, or `null` when `value` has another shape. */
export function bdOwnerDeletionCheckOf(
  value: unknown,
): BdOwnerDeletionCheck | null {
  if (
    !isRecord(value) ||
    typeof value['empty'] !== 'boolean' ||
    typeof value['blockingCount'] !== 'number' ||
    !isRecord(value['blockingCountByType']) ||
    !Array.isArray(value['blockingItems'])
  ) {
    return null;
  }
  const countByType: Record<string, number> = Object.fromEntries(
    Object.entries(value['blockingCountByType']).filter(
      (entry): entry is [string, number] => typeof entry[1] === 'number',
    ),
  );
  return {
    empty: value['empty'],
    blockingCount: value['blockingCount'],
    blockingCountByType: countByType,
    blockingItems: value['blockingItems']
      .filter(isBlockingItem)
      .map((item) => ({ ...item, name: item.name ?? null })),
  };
}

/** A company deletion check, or `null` when `value` has another shape. */
export function bdCompanyDeletionCheckOf(
  value: unknown,
): BdCompanyDeletionCheck | null {
  if (!isRecord(value)) {
    return null;
  }
  const resources = bdOwnerDeletionCheckOf(value['resources']);
  const activeGroupCount = value['activeGroupCount'];
  const activeDirectMemberCount = value['activeDirectMemberCount'];
  if (
    !resources ||
    typeof activeGroupCount !== 'number' ||
    typeof activeDirectMemberCount !== 'number'
  ) {
    return null;
  }
  return { resources, activeGroupCount, activeDirectMemberCount };
}

function isBlockingItem(value: unknown): value is BdOwnerDeletionBlockingItem {
  return (
    isRecord(value) &&
    typeof value['id'] === 'string' &&
    typeof value['type'] === 'string' &&
    (value['name'] === undefined ||
      value['name'] === null || typeof value['name'] === 'string') &&
    typeof value['folderId'] === 'string' &&
    Array.isArray(value['folderPath']) &&
    value['folderPath'].every((part) => typeof part === 'string') &&
    typeof value['inRecycleBin'] === 'boolean' &&
    typeof value['temporary'] === 'boolean'
  );
}

const PURGE_OUTCOMES: ReadonlySet<string> = new Set([
  'PURGED',
  'GONE',
  'SKIP',
  'NOT_SUPPORTED',
  'FAILED',
]);

/**
 * The purge task's `result`, which the job API hands over untyped. `null` when
 * it does not have the expected shape — the caller then relies on the
 * deletion check alone.
 */
export function bdContentPurgeResult(
  result: Record<string, unknown> | null | undefined,
): BdContentPurgeResult | null {
  if (!result) {
    return null;
  }
  const counts = result['counts'];
  const units = result['units'];
  if (
    !isRecord(counts) ||
    !Array.isArray(units) ||
    typeof result['empty'] !== 'boolean'
  ) {
    return null;
  }
  const purged = countOf(counts['purged']);
  const gone = countOf(counts['gone']);
  const skipped = countOf(counts['skipped']);
  const notSupported = countOf(counts['notSupported']);
  const failed = countOf(counts['failed']);
  return {
    counts: {
      purged,
      gone,
      skipped,
      notSupported,
      failed,
      total:
        countOf(counts['total']) ||
        purged + gone + skipped + notSupported + failed,
    },
    units: units.filter(isPurgeUnit).map((unit) => ({
      type: unit.type,
      id: unit.id,
      outcome: unit.outcome,
      reason: typeof unit.reason === 'string' ? unit.reason : null,
    })),
    allUnitsListed: result['allUnitsListed'] !== false,
    empty: result['empty'],
  };
}

function isPurgeUnit(value: unknown): value is {
  type: string;
  id: string;
  outcome: BdContentPurgeUnitOutcome;
  reason?: unknown;
} {
  return (
    isRecord(value) &&
    typeof value['type'] === 'string' &&
    typeof value['id'] === 'string' &&
    typeof value['outcome'] === 'string' &&
    PURGE_OUTCOMES.has(value['outcome'])
  );
}

function countOf(value: unknown): number {
  return typeof value === 'number' ? value : 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
